import React, { useEffect, useMemo, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { FiEdit2, FiTrash2 } from 'react-icons/fi';
import { ConfirmationDialog, PageHeader, useToast } from 'components/admin';
import { Button, Input, Select, Table } from 'components/common';
import {
    deleteSectionContentCard,
    getAdminSectionContentCards,
} from 'apis/adminSectionContentCards';
import { getAllAdminNavMenus } from 'apis/adminNavMenus';
import { normalizeUploadedImageUrl } from 'apis/uploadImage';
import { useActiveLanguagesQuery, useAdminDeleteMutation, useAdminListQuery } from 'hooks';
import '../../../styles/admin-shared.scss';

const SectionContentCardsList = () => {
    const [currentPage, setCurrentPage] = useState(1);
    const [pageSize] = useState(10);
    const [keyword, setKeyword] = useState('');
    const [isActiveFilter, setIsActiveFilter] = useState('');
    const [languageCodeFilter, setLanguageCodeFilter] = useState('vi');
    const [navMenuIdFilter, setNavMenuIdFilter] = useState('');
    const [deleteId, setDeleteId] = useState(null);
    const toast = useToast();
    const navigate = useNavigate();

    const queryParams = useMemo(() => ({
        pageIndex: currentPage,
        pageSize,
        keyword: keyword || undefined,
        isActive: isActiveFilter === '' ? undefined : isActiveFilter === 'true',
        languageCode: languageCodeFilter || undefined,
        navMenuId: navMenuIdFilter || undefined,
    }), [currentPage, pageSize, keyword, isActiveFilter, languageCodeFilter, navMenuIdFilter]);

    const { data = { items: [], total: 0, currentPage, pageSize }, isFetching, error } = useAdminListQuery('section-content-cards', queryParams, getAdminSectionContentCards);
    const { data: languages = [] } = useActiveLanguagesQuery();
    const { data: menuData = { items: [] } } = useAdminListQuery('nav-menus-options', { pageSize: 1000, languageCode: languageCodeFilter || 'vi' }, getAllAdminNavMenus);
    const menus = menuData.items;
    const deleteMutation = useAdminDeleteMutation('section-content-cards', deleteSectionContentCard);

    useEffect(() => {
        setCurrentPage(1);
    }, [keyword, isActiveFilter, languageCodeFilter, navMenuIdFilter]);

    useEffect(() => {
        if (error) toast.error(error.message || 'Tải dữ liệu thất bại');
    }, [error, toast]);

    const getMenuTitle = (menuId) => {
        const menu = menus.find(m => Number(m.id) === Number(menuId));
        const activeLangId = languages.find(l => l.code === (languageCodeFilter || 'vi'))?.id;
        const translation = menu?.translations?.find(t => t.languageId === activeLangId) || menu?.translations?.[0];
        return translation?.title || translation?.name || menu?.title || menu?.name || `Menu ${menuId}`;
    };

    const handleDelete = async () => {
        try {
            const res = await deleteMutation.mutateAsync(deleteId);
            if (res.success) toast.success('Khóa card thành công');
            else toast.error(res.message || 'Khóa card thất bại');
        } catch {
            toast.error('Khóa card thất bại');
        }
        setDeleteId(null);
    };

    const columns = [
        { field: 'id', header: 'ID' },
        {
            header: 'Ảnh',
            render: (row) => {
                const imageUrl = normalizeUploadedImageUrl(
                    row.imgURL || row.imgUrl || row.imageUrl || ''
                );

                return imageUrl
                    ? <img src={imageUrl} alt="" style={{ width: 52, height: 40, objectFit: 'contain', borderRadius: 4 }} />
                    : '—';
            },
        },
        { header: 'Tiêu đề', render: (row) => row.title || row.translations?.[0]?.title || '—' },
        { header: 'Phụ đề', render: (row) => row.subtitle || row.translations?.[0]?.subtitle || '—' },
        {
            header: 'Trang',
            render: (row) => {
                const pages = row.pages || [];
                if (!pages.length) return '—';
                return pages.map(page => getMenuTitle(page.navMenuId)).join(', ');
            },
        },
        {
            header: 'Màu',
            render: (row) => (
                <div style={{ display: 'flex', gap: 8, alignItems: 'center' }}>
                    <span style={{ width: 18, height: 18, borderRadius: 4, border: '1px solid #d1d5db', background: row.color || '#fff' }} />
                    <span style={{ width: 18, height: 18, borderRadius: 4, border: '1px solid #d1d5db', background: row.textColor || '#123D2A' }} />
                </div>
            ),
        },
        { header: 'Trạng thái', render: (row) => <span className={`status-badge ${row.isActive ? 'active' : 'inactive'}`}>{row.isActive ? 'Hoạt động' : 'Không hoạt động'}</span> },
        {
            header: 'Thao tác',
            render: (row) => (
                <div className="action-buttons">
                    <button className="icon-btn icon-btn--edit" onClick={() => navigate(`/admin/section-content-cards/edit/${row.id}`)} title="Sửa"><FiEdit2 size={15} /></button>
                    <button className="icon-btn icon-btn--delete" onClick={() => setDeleteId(row.id)} title="Khóa"><FiTrash2 size={15} /></button>
                </div>
            ),
        },
    ];

    return (
        <div className="admin-page">
            <PageHeader
                title="Card nội dung theo trang"
                subtitle="Quản lý card dùng chung cho các section trên website"
                action={<Button onClick={() => navigate('/admin/section-content-cards/create')}>+ Thêm Card</Button>}
            />
            <div className="filters-bar">
                <div className="filter-item"><Input placeholder="Tìm kiếm..." value={keyword} onChange={e => setKeyword(e.target.value)} /></div>
                <div className="filter-item">
                    <Select value={isActiveFilter} onChange={e => setIsActiveFilter(e.target.value)} options={[{ value: '', label: 'Tất cả trạng thái' }, { value: 'true', label: 'Hoạt động' }, { value: 'false', label: 'Không hoạt động' }]} />
                </div>
                <div className="filter-item">
                    <Select value={languageCodeFilter} onChange={e => setLanguageCodeFilter(e.target.value)} options={languages.map(l => ({ value: l.code, label: l.name }))} />
                </div>
                <div className="filter-item">
                    <Select
                        value={navMenuIdFilter}
                        onChange={e => setNavMenuIdFilter(e.target.value)}
                        options={[{ value: '', label: 'Tất cả trang' }, ...menus.map(m => ({ value: m.id, label: getMenuTitle(m.id) }))]}
                    />
                </div>
            </div>
            <Table columns={columns} data={data.items} loading={isFetching} pagination={{ currentPage: data.currentPage, totalPages: Math.ceil(data.total / data.pageSize) || 1, onPageChange: setCurrentPage }} />
            <ConfirmationDialog open={!!deleteId} onClose={() => setDeleteId(null)} onConfirm={handleDelete} title="Khóa Card" message="Bạn có chắc chắn muốn khóa card này không?" />
        </div>
    );
};

export default SectionContentCardsList;
